import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../../../services/navigation.service';
//import moment from 'moment';

import { EQUIPMENT_ID, ROUTES } from '../../../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { CalendarIcon, EquipmentIcon, MultiSelect, RootState, SectorIcon, Skill } from '@umanforge/common';

import { HomeTopBar } from '../../../components/menu/home_top_bar.component';
import { Breadcrumb } from '../../../components/menu/breadcrumb.component';
import { Button } from '../../../components/inputs/button.component';
import { Input } from '../../../components/inputs/input.component';
import { Select } from '../../../components/inputs/select.component';
//import { ExpirationEquipment } from '../../../components/users/expiration_equipment.component';
import { Loading } from '../../../components/loading.component';
import { ErrorModal } from 'components/modals/error_modal.component';
import { BodyConfirmModal } from 'components/modals/body/body_confirm_modal.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { ClientsIcon } from '../../../components/icons/clients_icon.component';
import { SaveIcon } from '../../../components/icons/save_icon.component';
import { BinIcon } from '../../../components/icons/bin_icon.component';
import { SitesIcon } from '../../../components/icons/sites_icon.component';
import { AbilitiesIcon } from '../../../components/icons/abilities_icon.component';
import { useDetailsEquipment } from '../hooks/details_equipment.hook';
import { ExpirationDate } from '../components/expiration_date.component';
import { SkillsIcon } from 'components/icons/skills_icon.component';
import { ILUOSkill } from 'components/users/iluo_skill.component';



export function DetailsEquipmentView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const dispatch = useDispatch();


  const {
    equipment,
    sectorOfEquipment,
    siteOfSector,
    clientOfSite,
    modifyEquipment, setModifyEquipment,
    canPut,
    hasChanged,
    isAdminOfSite,
    confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen,
    isLoading,
    deleteEquipment,
    onSaveClick,

    selectedAbilities, setSelectedAbilities,
    getAbilities,

    selectedSkills, setSelectedSkills,
    getSkills
  } = useDetailsEquipment();

  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChanged } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'modify_equipment' ) } needConfirmationToNav={ hasChanged }/>
        <Breadcrumb
          needConfirmationToNav={ hasChanged }
          links={
            [
              { label: t( 'common:equipment' ), route: ROUTES.EQUIPMENTS, icon: ( <EquipmentIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: equipment ? equipment.name : t( 'modify_equipment' ), route: ROUTES.EQUIPMENT.replace( EQUIPMENT_ID, params.equipmentId ?? '' ), icon: ( <EquipmentIcon className='h-[14px] select-none cursor-pointer'/> ) }
            ]
          }
        />
        {
          !isLoading && equipment ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full mb-12 items-center justify-center p-4 px-[10%]'>

                <div className='w-full'>
                  <Select
                    required
                    disabled={ true }
                    value={ clientOfSite }
                    label={ t( 'Client' ) }
                    placeholder={ t( 'No associated clients' ) }
                    icon={ <ClientsIcon className='w-7 h-7' /> }
                    options={ [ clientOfSite ] }
                  />
                </div>

                <div className='w-full'>
                  <Select
                    required
                    disabled={ true }
                    value={ siteOfSector }
                    label={ t( 'Site' ) }
                    placeholder={ t( 'No associated sites' ) }
                    icon={ <SitesIcon className='w-7 h-7' /> }
                    options={ [ siteOfSector ] }
                  />
                </div>

                <div className='w-full'>
                  <Select
                    required
                    disabled={ true }
                    value={ sectorOfEquipment }
                    label={ t( 'Sector' ) }
                    placeholder={ t( 'No associated sectors' ) }
                    icon={ <SectorIcon className='w-7 h-7' /> }
                    options={ [ sectorOfEquipment ] }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    required
                    maxLength={ MAX_LENGTH_ENTITY.UF_EQUIPMENT_NAME }
                    label={ t( 'equipment_name' ) }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'common:equipment' ) + '...' }
                    icon={ <EquipmentIcon className='w-7 h-7' /> }
                    value={ equipment.name ?? ''}
                    onChange={ newEquipmentName => {
                      modifyEquipment.name = newEquipmentName;
                      setModifyEquipment( structuredClone( modifyEquipment ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    required
                    maxLength={ MAX_LENGTH_ENTITY.UF_EQUIPMENT_DESCRIPTION }
                    label={ t( 'equipment_description' ) }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'equipment_description' ) + '...' }
                    icon={ <EquipmentIcon className='w-7 h-7' /> }
                    value={ equipment.description ?? ''}
                    onChange={ newEquipmentDescription => {
                      modifyEquipment.description = newEquipmentDescription;
                      setModifyEquipment( structuredClone( modifyEquipment ) );
                    } }
                  />
                </div>

                <div className='w-1/4 min-w-[225px] self-start'>
                  <ExpirationDate
                    required
                    label={ t( 'expiration_date' ) }
                    disabled={ !isAdminOfSite }
                    icon={ <CalendarIcon className='w-7 h-7' /> }
                    equipment={equipment}
                    onChange={ newValidityDate => {
                      modifyEquipment.expirationDate = newValidityDate;
                      setModifyEquipment( structuredClone( modifyEquipment ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <MultiSelect
                    label={ t( 'common:authorization' ) + '(' + selectedAbilities.length + ')' }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'No associated authorizations' ) }
                    icon={ <AbilitiesIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedAbilities }
                    getValues={ getAbilities }
                    onChange={ options => {
                      setSelectedAbilities( options );
                    } }
                    extra={ undefined }
                  />
                </div>
                <div className='w-full'>
                  <MultiSelect
                    label={ t( 'common:skill' ) + '(' + selectedSkills.length + ')' }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'No associated skills' ) }
                    icon={ <SkillsIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedSkills }
                    getValues={ getSkills }
                    onChange={ options => {
                      setSelectedSkills( options );
                    } }
                    extra={
                      skill => {
                        return (
                          <ILUOSkill
                            skill={ ( skill.object as unknown as Skill ) }
                            onChange={ changedSkill => {
                              const findedSkill = selectedSkills.find( item => { return changedSkill.id == item.object.id;  } );
                              if ( findedSkill ) {
                                findedSkill.object.value = changedSkill.value;
                              }
                              setSelectedSkills( structuredClone( selectedSkills ) );
                            } }
                          />
                        );
                      }
                    }
                  />
                </div>

                <div className='flex justify-center w-full h-14 mt-10'>
                  {
                    isAdminOfSite ?
                      <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                        <Button
                          label={ t( 'Delete' ) }
                          icon={ <BinIcon className='h-[24px] w-[24px] stroke-white'/> }
                          color='#f87171'
                          colorHover='#ef4444'
                          onClick={ () => { setConfirmDeleteModalIsOpen( true ); } }
                        />
                      </div>
                      :
                      null
                  }
                  <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                    <Button
                      label={ t( 'save' ) }
                      disable={ !canPut }
                      icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                      color='#0bb173'
                      colorHover='#088D5C'
                      onClick={ onSaveClick }
                    />
                  </div>
                </div>
              </form>

              { confirmDeleteModalIsOpen ?
                <ErrorModal
                  title={ t( 'Deletion of {0} !' ).replace( '{0}', equipment.name )  }
                  isOpen={ confirmDeleteModalIsOpen }
                  onClose={ () => { setConfirmDeleteModalIsOpen( false ); } }
                >
                  <BodyConfirmModal
                    onCancel={ () => { setConfirmDeleteModalIsOpen( false ); } }
                    onConfirm={ async () => {
                      const isDeleted = await deleteEquipment();
                      if ( isDeleted ) {
                        NavigationService.toEquipments( navigate, false );
                      }
                      setConfirmDeleteModalIsOpen( false );
                    } }
                  >
                    <span className='flex flex-col w-full h-full text-[14px] font-[475] text-gray-600 p-3'>
                      { t( 'are_you_sure_you_want_to_delete_this_equipment' ) }
                    </span>
                  </BodyConfirmModal>
                </ErrorModal>
                :
                null
              }
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}